import { $event_subscribe } from "@/core/subscribe";
import { uuid } from "@/utils/util";

/**
 * 事件 API 接口
 *
 * 提供跨窗口/iframe 消息通信和页面 ID 管理功能。
 */
export interface EventApi {
  /**
   * 获取当前页面的唯一 ID
   *
   * @returns 页面唯一标识符 (UUID)
   */
  getId(): string;

  /**
   * 向父窗口发送消息
   *
   * @param t - 要发送的数据
   * @param r - 目标源，默认为 "*"
   *
   * @example
   * ```typescript
   * $event.post({ type: 'hello', message: 'world' });
   * ```
   */
  post<T>(t: T, r?: string): void;

  /**
   * 向指定 iframe 发送消息
   *
   * @param iframe - 目标 iframe 元素
   * @param t - 要发送的数据
   * @param r - 目标源，默认为 "*"
   *
   * @example
   * ```typescript
   * const iframe = document.querySelector('iframe');
   * $event.postIframe(iframe, { type: 'data' });
   * ```
   */
  postIframe<T>(iframe: HTMLIFrameElement, t: T, r?: string): void;
}

/**
 * 事件收集器
 *
 * 单例类，负责：
 * - 监听浏览器事件 (message, click, beforeunload, hashchange)
 * - 管理页面唯一 ID
 * - 提供 postMessage 封装
 * - 将浏览器事件转发到事件订阅系统
 */
class EventCollect implements EventApi {
  private static instance: EventCollect | null = null;
  /** 页面 URL 到 ID 的映射 */
  ids: { [key: string]: string } = {};

  private constructor() {
    this.init();
    this.ids[window.location.href] = uuid();
  }

  /**
   * 获取 EventCollect 单例实例
   *
   * @returns EventCollect 实例
   */
  static create(): EventCollect {
    if (!EventCollect.instance) {
      EventCollect.instance = new EventCollect();
    }
    return EventCollect.instance;
  }

  getId(): string {
    let id = this.ids[window.location.href];
    if (id === undefined) {
      id = uuid();
      this.ids[window.location.href] = id;
    }
    return id;
  }

  /**
   * 初始化事件监听器
   *
   * 监听以下浏览器事件并转发到事件订阅系统：
   * - beforeunload: 页面关闭
   * - hashchange: URL hash 变化
   * - click: 页面点击
   * - message: 跨窗口消息
   */
  init(): void {
    window.addEventListener("beforeunload", () => {
      $event_subscribe.emit("page_close", this.ids[window.location.href]);
    });
    window.addEventListener("hashchange", (): void => {
      if (this.ids[window.location.href] === undefined) {
        this.ids[window.location.href] = uuid();
      }
    });
    document.addEventListener("click", (ev) => {
      $event_subscribe.emit("page_click", ev);
    });
    window.addEventListener(
      "message",
      (e) => {
        $event_subscribe.emit("message", e.data);
      },
      false,
    );
  }

  post<T>(t: T, r: string = "*"): void {
    window.parent.postMessage(t, r);
  }

  postIframe<T>(iframe: HTMLIFrameElement, t: T, r: string = "*"): void {
    iframe.contentWindow?.postMessage(t, r);
  }
}

export const $event: EventApi = EventCollect.create();
